/**
 * Push notifications on this device: "Enable notifications" (permission from a user gesture → FCM token →
 * POST /v1/devices through the DataSource), keeping an enabled device registered (token rotation, weekly refresh),
 * and turning it off or signing out (DELETE /v1/devices/:token, then the token is invalidated at FCM so nothing more
 * arrives even if the API call never made it). The registration is remembered per person, so signing in as someone
 * else starts with notifications off. A `local` PushPort (desktop: notifications come over the WebSocket) only asks
 * for the permission; nothing is registered with the server. There they are on by default once the OS allows them
 * (Slack), until the person turns them off on this device; when the OS blocks them, `blockedNotice` asks to fix that
 * in the OS settings ("Notifications are turned off for Bundle").
 */
import type { DevicePlatform, RegisterDeviceRequest } from "@bundle/contracts";
import type { Clock, DataSource, KeyValueStorage, PushPort } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

/**
 * unsupported / unconfigured: nothing to offer here · off: offer "Enable notifications" · on: registered ·
 * blocked: the user denied permission (only the browser/OS settings can undo it).
 */
export type NotificationsStatus = "unsupported" | "unconfigured" | "off" | "on" | "blocked";

export type NotificationsState = {
  status: NotificationsStatus;
  busy: boolean;
  /** The last enable attempt failed (network, FCM); "off" stays and the user can try again. */
  failed: boolean;
  /**
   * Local ports only (desktop): offer "Turn on notifications" (a dismissible prompt) while they are off. The desktop
   * shows nothing until the person turns them on, and the settings screen may not be there to do it.
   */
  offer?: boolean;
  /**
   * Local ports only: the OS blocks notifications for the app, and the person hasn't closed the warning in the last
   * week ("macOS is blocking Bundle's notifications.", Open System Settings).
   */
  blockedNotice?: boolean;
  /** The port can open the OS page where notifications are allowed (`openSettings()`). */
  canOpenSettings?: boolean;
  /** The port can show a test notification (`sendTest()`). */
  canTest?: boolean;
  /** Desktop: the OS whose settings hold the switch ("macOS is blocking…", "Off in macOS"). */
  os?: "macos" | "windows";
};

export type NotificationsDeps = {
  push: PushPort;
  data: DataSource;
  storage: KeyValueStorage;
  clock: Clock;
  platform: DevicePlatform;
  /** The signed-in person (undefined = signed out). */
  userId: () => string | undefined;
  /** App language (BCP 47), so push text matches it. */
  locale: () => string;
  appVersion?: string;
  timeZone?: () => string | undefined;
};

export const PUSH_REGISTRATION_KEY = "bundle.push.v1";
/** The person dismissed the "Turn on notifications" offer on this device. */
export const NOTIFY_OFFER_DISMISSED_KEY = "bundle.notifyOffer.v1";
/** Local ports: the people who turned notifications off on this device (JSON array of user ids); on for everyone else. */
export const NOTIFY_LOCAL_OFF_KEY = "bundle.notifyLocalOff.v1";
/** When the person closed the "blocked by the OS" warning (ms); it stays away for `BLOCKED_SNOOZE_MS`. */
export const NOTIFY_BLOCKED_CLOSED_KEY = "bundle.notifyBlockedClosed.v1";
/** × on the blocked warning hides it for a week (Slack); the Settings dot stays while the OS still blocks them. */
export const BLOCKED_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
/** Re-register an enabled device at least this often (FCM expires tokens that go unused). */
export const PUSH_REFRESH_MS = 7 * 24 * 60 * 60 * 1000;

type Registration = { userId: string; token: string; at: number };

function parseRegistration(raw: string | undefined): Registration | undefined {
  if (raw === undefined) return undefined;
  try {
    const v = JSON.parse(raw) as Partial<Registration>;
    if (typeof v.userId !== "string" || typeof v.token !== "string" || typeof v.at !== "number") return undefined;
    return { userId: v.userId, token: v.token, at: v.at };
  } catch {
    return undefined;
  }
}

/** Status from platform support, permission and whether this person's device is registered. */
export function notificationsStatus(push: PushPort, registeredForUser: boolean): NotificationsStatus {
  const availability = push.availability();
  if (availability !== "available") return availability;
  const permission = push.permission();
  if (permission === "denied") return "blocked";
  return permission === "granted" && registeredForUser ? "on" : "off";
}

export class NotificationsViewModel extends ViewModel<NotificationsState> {
  private readonly deps: NotificationsDeps;

  constructor(deps: NotificationsDeps) {
    super({ status: "unsupported", busy: false, failed: false });
    this.deps = deps;
    this.refresh();
  }

  /** Re-reads support/permission/registration (after sign-in changes, or when the tab is visible again). */
  refresh(): void {
    const reg = this.registration();
    const user = this.deps.userId();
    const on = user !== undefined && (reg?.userId === user || (this.deps.push.local === true && !this.localOff().includes(user)));
    this.update({ status: notificationsStatus(this.deps.push, on) });
  }

  /** × on the blocked warning. */
  dismissBlocked(): void {
    this.deps.storage.set(NOTIFY_BLOCKED_CLOSED_KEY, String(this.deps.clock.now()));
    this.update({});
  }

  /** "Send a test notification" (only where the port can show one: the desktop app). */
  sendTest(title: string, body: string): void {
    this.deps.push.test?.(title, body);
  }

  /** "Open System Settings": where the OS permission is changed. */
  openSettings(): void {
    this.deps.push.openSettings?.();
  }

  /** "Not now" on the offer: it doesn't come back on this device (the menu and settings still turn them on). */
  dismissOffer(): void {
    this.deps.storage.set(NOTIFY_OFFER_DISMISSED_KEY, "1");
    this.update({});
  }

  /** Keeps `offer` in step with the status (local ports only; absent otherwise). */
  protected override update(patch: Partial<NotificationsState>): void {
    if (this.deps.push.local !== true) {
      super.update(patch);
      return;
    }
    const status = patch.status ?? this.state.status;
    super.update({
      ...patch,
      offer: status === "off" && this.deps.storage.get(NOTIFY_OFFER_DISMISSED_KEY) === undefined,
      blockedNotice: status === "blocked" && !this.blockedSnoozed(),
      canTest: this.deps.push.test !== undefined,
      ...(this.deps.push.os === undefined ? {} : { os: this.deps.push.os }),
      canOpenSettings: this.deps.push.openSettings !== undefined,
    });
  }

  /** "Enable notifications". Call directly from the click/tap handler (the permission prompt needs the gesture). */
  async enable(): Promise<void> {
    const user = this.deps.userId();
    if (user === undefined || this.state.busy || (this.state.status !== "off" && this.state.status !== "on")) return;
    // Ask first, synchronously inside the gesture; nothing is awaited before this call.
    const asked = this.deps.push.permission() === "granted" ? Promise.resolve("granted" as const) : this.deps.push.requestPermission();
    this.update({ busy: true, failed: false });
    try {
      const permission = await asked;
      if (permission !== "granted") {
        this.update({ busy: false, status: permission === "denied" ? "blocked" : "off" });
        return;
      }
      if (this.deps.push.local === true) {
        this.remember(user, "");
        this.setLocalOff(user, false);
      }
      else await this.register(user, await this.deps.push.token());
      this.update({ busy: false, status: "on" });
    } catch {
      this.update({ busy: false, failed: true });
      this.refresh();
    }
  }

  /** Turns notifications off for this device. */
  async disable(): Promise<void> {
    if (this.state.busy) return;
    this.update({ busy: true, failed: false });
    const user = this.deps.userId();
    if (this.deps.push.local === true && user !== undefined) this.setLocalOff(user, true);
    await this.forget(true);
    this.update({ busy: false });
    this.refresh();
  }

  /**
   * Startup and after sign-in: an enabled device stays registered — a rotated token is registered (and the old one
   * dropped), and the registration is refreshed weekly. Failures are retried on the next sync.
   */
  async sync(): Promise<void> {
    const user = this.deps.userId();
    const reg = this.registration();
    this.refresh();
    if (user === undefined || reg === undefined || reg.userId !== user || this.state.status !== "on") return;
    if (this.deps.push.local === true) return;
    try {
      const token = await this.deps.push.token();
      if (token === reg.token && this.deps.clock.now() - reg.at < PUSH_REFRESH_MS) return;
      await this.register(user, token);
      if (token !== reg.token) await this.deps.data.unregisterDevice(reg.token).catch(() => undefined);
    } catch {
      // Offline or FCM unavailable: the stored registration still stands; try again next time.
    }
  }

  /** Before signing out, while the session can still authenticate the DELETE. */
  async signOut(): Promise<void> {
    await this.forget(true);
    this.refresh();
  }

  /**
   * The signed-in person changed without signOut() (session expired, signed out elsewhere): a registration that
   * isn't theirs is dropped and its token invalidated (no API call — the session is gone).
   */
  sessionChanged(): void {
    const reg = this.registration();
    if (reg !== undefined && reg.userId !== this.deps.userId()) void this.forget(false);
    this.refresh();
  }

  private blockedSnoozed(): boolean {
    const at = Number(this.deps.storage.get(NOTIFY_BLOCKED_CLOSED_KEY));
    return Number.isFinite(at) && at > 0 && this.deps.clock.now() - at < BLOCKED_SNOOZE_MS;
  }

  private localOff(): string[] {
    try {
      const v: unknown = JSON.parse(this.deps.storage.get(NOTIFY_LOCAL_OFF_KEY) ?? "[]");
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  }

  private setLocalOff(userId: string, off: boolean): void {
    const rest = this.localOff().filter((u) => u !== userId);
    this.deps.storage.set(NOTIFY_LOCAL_OFF_KEY, JSON.stringify(off ? [...rest, userId] : rest));
  }

  private registration(): Registration | undefined {
    return parseRegistration(this.deps.storage.get(PUSH_REGISTRATION_KEY));
  }

  private async register(userId: string, token: string): Promise<void> {
    const req: RegisterDeviceRequest = { platform: this.deps.platform, token, locale: this.deps.locale() };
    if (this.deps.appVersion !== undefined) req.appVersion = this.deps.appVersion;
    const tz = this.deps.timeZone?.();
    if (tz !== undefined) req.timeZone = tz;
    await this.deps.data.registerDevice(req);
    this.remember(userId, token);
  }

  private remember(userId: string, token: string): void {
    const reg: Registration = { userId, token, at: this.deps.clock.now() };
    this.deps.storage.set(PUSH_REGISTRATION_KEY, JSON.stringify(reg));
  }

  /** Drops the stored registration; best effort: DELETE it at the API (when `unregister`), invalidate the token. */
  private async forget(unregister: boolean): Promise<void> {
    const reg = this.registration();
    if (reg === undefined) return;
    this.deps.storage.remove(PUSH_REGISTRATION_KEY);
    if (this.deps.push.local === true) return;
    await Promise.allSettled([
      unregister ? this.deps.data.unregisterDevice(reg.token) : Promise.resolve(),
      this.deps.push.deleteToken(),
    ]);
  }
}
